import React, { forwardRef, ReactNode } from "react";
import { Box, Tooltip, styled } from "@mui/material";
import { colors } from "../../config";

interface OutlinedButtonProps {
  children: ReactNode;
  tooltip?: string;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  fullWidth?: boolean;
  width?: string | number;
  height?: string | number;
  padding?: string;
}

interface StyledButtonProps {
  disabled?: boolean;
  fullWidth?: boolean;
  width?: string | number;
  height?: string | number;
  padding?: string;
}

const GradientBorderWrapper = styled(Box)<StyledButtonProps>(
  ({ disabled, fullWidth, width, height }) => ({
    position: "relative",
    display: "inline-flex",
    width: fullWidth ? "100%" : width || "auto",
    height: height || "25px",
    borderRadius: "5px",
    padding: "2px",
    background: colors.gradient.primary,
    opacity: disabled ? 0.5 : 1,
    transition: "opacity 0.2s ease, transform 0.2s ease",

    "&:hover": {
      opacity: disabled ? 0.5 : 0.9,
      transform: disabled ? "none" : "scale(1.02)",
    },

    "&:active": {
      transform: disabled ? "none" : "scale(0.98)",
    },
  }),
);

const StyledButton = styled("button")<StyledButtonProps>(
  ({ padding, disabled }) => ({
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: colors.background.white,
    border: "none",
    borderRadius: "3px",
    padding: padding || "0 5px",
    cursor: disabled ? "not-allowed" : "pointer",
    fontSize: "11px",
    fontWeight: 500,
    fontFamily: "inherit",
    transition: "opacity 0.2s ease",

    "&:disabled": {
      cursor: "not-allowed",
    },
  }),
);

const GradientText = styled("span")({
  backgroundImage: colors.gradient.primary,
  backgroundClip: "text",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  color: "transparent",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  display: "inline-block",
  fontWeight: "inherit",
  fontSize: "inherit",
});

export const OutlinedButton = forwardRef<HTMLButtonElement, OutlinedButtonProps>(
  (
    {
      children,
      tooltip,
      onClick,
      disabled = false,
      fullWidth = false,
      width,
      height,
      padding,
    },
    ref,
  ) => {
    const button = (
      <GradientBorderWrapper
        disabled={disabled}
        fullWidth={fullWidth}
        width={width}
        height={height}
      >
        <StyledButton
          ref={ref}
          disabled={disabled}
          padding={padding}
          onClick={disabled ? undefined : onClick}
        >
          <GradientText>{children}</GradientText>
        </StyledButton>
      </GradientBorderWrapper>
    );

    if (tooltip) {
      return (
        <Tooltip title={tooltip} arrow>
          <Box component="span" sx={{ display: "inline-flex" }}>
            {button}
          </Box>
        </Tooltip>
      );
    }

    return button;
  },
);

OutlinedButton.displayName = "OutlinedButton";

export default OutlinedButton;

